import React from 'react';
import { Box, Typography } from '@mui/material';
import { resolveSlotValues, getDTWidgetSlots } from '../../registry/dtWidgetTypes';
import { useDTDemoMap } from '../../hooks/useDTDemoTick';
import { hasLiveSlotValue } from '../../utils/dtDemoFallback';
import { dtWidgetFilledColumnSx } from '../../styles/dtWidgetStyleTokens';

const DEFAULT_SLOTS = getDTWidgetSlots('dt-status-indicators');

const STATUS_DEFS = [
  { slotKey: 'thermalStatus', defaultLabel: 'Thermal', activeLabel: 'HIGH', inactiveLabel: 'OK', activeColor: 'red', inactiveColor: 'green', activeWhen: (v) => parseFloat(v) === 1 },
  { slotKey: 'tripInfo', defaultLabel: 'Trip Information', activeLabel: 'TRP', inactiveLabel: 'OK', activeColor: 'red', inactiveColor: 'green', activeWhen: (v) => parseFloat(v) === 1 },
  { slotKey: 'status', defaultLabel: 'Status', activeLabel: 'ON', inactiveLabel: 'OFF', activeColor: 'green', inactiveColor: 'red', activeWhen: (v) => parseFloat(v) === 1 },
];

const VOLTAGE_SLOT_KEYS = ['L1', 'L2', 'L3', 'Average'];
const CURRENT_SLOT_KEYS = ['RMS_R', 'RMS_Y', 'RMS_B', 'THD_R', 'THD_Y', 'THD_B'];

function DTStatusIndicators({ widget, data, theme }) {
  const isDark = theme === 'dark';
  const tagprops = widget?.props?.extended?.tagprops || [];
  const liveData = Array.isArray(data) ? data : [];
  const slotValues = resolveSlotValues(tagprops, liveData);
  const demoMap = useDTDemoMap('dt-status-indicators');

  const voltageSlots = DEFAULT_SLOTS.filter((s) => VOLTAGE_SLOT_KEYS.includes(s.key));
  const currentSlots = DEFAULT_SLOTS.filter((s) => CURRENT_SLOT_KEYS.includes(s.key));

  return (
    <Box sx={{ ...dtWidgetFilledColumnSx, px: 1 }}>
      <Typography variant="subtitle2" sx={{ color: isDark ? '#fff' : '#000', fontWeight: 700, mb: 0.5, borderBottom: '1px solid #B0B0B0', flexShrink: 0 }}>
        Status
      </Typography>
      <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 0.5, mb: 0.75 }}>
        {STATUS_DEFS.map(({ slotKey, defaultLabel, activeLabel, inactiveLabel, activeColor, inactiveColor, activeWhen }) => {
          const resolved = slotValues[slotKey];
          const raw = hasLiveSlotValue(resolved) ? resolved.value : demoMap[slotKey];
          const label = resolved?.alias || defaultLabel;
          const isActive = activeWhen(raw);
          return (
            <Box key={slotKey} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: isDark ? '#fff' : '#000' }}>{label}</Typography>
              <Box sx={{ px: 1.5, py: 0.3, borderRadius: 5, backgroundColor: isActive ? activeColor : inactiveColor, color: '#fff', fontWeight: 700, fontSize: 11 }}>
                {isActive ? activeLabel : inactiveLabel}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Typography variant="subtitle2" sx={{ color: isDark ? '#fff' : '#000', fontWeight: 700, mb: 0.25, borderBottom: '1px solid #B0B0B0', flexShrink: 0 }}>
          Voltage (V)
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
          {voltageSlots.map((slot) => {
            const resolved = slotValues[slot.key];
            const liveOk = hasLiveSlotValue(resolved);
            const displayVal = liveOk ? resolved.value : demoMap[slot.key] ?? '—';
            return (
              <Box key={slot.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: '1 1 0', minHeight: 0 }}>
                <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#555' }}>{resolved?.alias || slot.label}</Typography>
                <Typography variant="body2" sx={{ color: isDark ? '#fff' : '#000', fontWeight: 600 }}>
                  {displayVal}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', mt: 0.5 }}>
        <Typography variant="subtitle2" sx={{ color: isDark ? '#fff' : '#000', fontWeight: 700, mb: 0.25, borderBottom: '1px solid #B0B0B0', flexShrink: 0 }}>
          Current (A)
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
          {currentSlots.map((slot) => {
            const resolved = slotValues[slot.key];
            const liveOk = hasLiveSlotValue(resolved);
            const displayVal = liveOk ? resolved.value : demoMap[slot.key] ?? '—';
            return (
              <Box key={slot.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: '1 1 0', minHeight: 0 }}>
                <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#555' }}>{resolved?.alias || slot.label}</Typography>
                <Typography variant="body2" sx={{ color: isDark ? '#fff' : '#000', fontWeight: 600 }}>
                  {displayVal}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

export default React.memo(DTStatusIndicators);
